import { useMemo, type CSSProperties } from 'react';
import { useTheme } from '@vitness/theme';

export interface VitnessClerkTheme {
  accent: string;
  accentSafe: string;
}

const clerkStyle = (properties: CSSProperties) => properties;

/**
 * Shared popover surface — a Vitness dropdown card. Used for every Clerk
 * popover/menu: UserButton, OrganizationSwitcher, and the OrganizationProfile
 * role/row-action menus.
 */
const vitnessPopoverSurface = clerkStyle({
  background: 'var(--vn-surface-card)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: '8px',
  boxShadow: 'var(--vn-shadow-dropdown)',
});

/** Shared popover/menu item label styling. */
const vitnessPopoverItem = clerkStyle({
  fontSize: '12px',
  fontWeight: 650,
  color: 'var(--vn-text-secondary)',
});

function createClerkVariables({
  accentSafe,
}: VitnessClerkTheme) {
  return {
    colorPrimary: accentSafe,
    colorBackground: 'var(--vn-surface-card)',
    colorForeground: 'var(--vn-text-primary)',
    colorMutedForeground: 'var(--vn-text-secondary)',
    colorInput: 'var(--vn-surface-input)',
    colorInputForeground: 'var(--vn-text-primary)',
    colorDanger: 'var(--vn-danger)',
    borderRadius: '8px',
    fontFamily: 'var(--vn-font-body)',
    fontFamilyButtons: 'var(--vn-font-body)',
  };
}

function createClerkFormElements({
  accent,
  accentSafe,
}: VitnessClerkTheme) {
  return {
    formFieldLabel: clerkStyle({
      fontSize: '11px',
      fontWeight: 650,
      color: 'var(--vn-text-secondary)',
    }),
    formFieldInput: clerkStyle({
      minHeight: '42px',
      padding: '10px 12px',
      color: 'var(--vn-text-primary)',
      background: 'var(--vn-surface-input)',
      border: '1px solid var(--vn-border-default)',
      boxShadow: 'none',
    }),
    formButtonPrimary: clerkStyle({
      minHeight: '42px',
      color: '#09090B',
      background: accent,
      boxShadow: 'none',
      fontSize: '12px',
      fontWeight: 750,
    }),
    formButtonReset: clerkStyle({
      color: accentSafe,
    }),
  };
}

/**
 * Builds the compact Clerk appearance used inside the Vitness authentication
 * shell. This preset intentionally removes Clerk's outer card chrome.
 */
export function createVitnessClerkAppearance({
  accent,
  accentSafe,
}: VitnessClerkTheme) {
  return {
    variables: {
      ...createClerkVariables({ accent, accentSafe }),
      colorBackground: 'transparent',
    },
    elements: {
      ...createClerkFormElements({ accent, accentSafe }),
      rootBox: clerkStyle({
        width: '100%',
      }),
      cardBox: clerkStyle({
        width: '100%',
        boxShadow: 'none',
      }),
      card: clerkStyle({
        width: '100%',
        padding: 0,
        background: 'transparent',
        border: 0,
        boxShadow: 'none',
      }),
      header: clerkStyle({
        padding: 0,
      }),
      headerTitle: clerkStyle({
        fontFamily: 'var(--vn-font-display)',
        fontSize: '26px',
        fontWeight: 750,
        letterSpacing: '-0.035em',
        color: 'var(--vn-text-primary)',
      }),
      headerSubtitle: clerkStyle({
        fontSize: '13px',
        color: 'var(--vn-text-secondary)',
      }),
      socialButtonsBlockButton: clerkStyle({
        minHeight: '42px',
        color: 'var(--vn-text-primary)',
        background: 'var(--vn-surface-raised)',
        border: '1px solid var(--vn-border-default)',
        boxShadow: 'none',
      }),
      socialButtonsBlockButtonText: clerkStyle({
        fontSize: '12px',
        fontWeight: 650,
      }),
      dividerLine: clerkStyle({
        background: 'var(--vn-border-default)',
      }),
      dividerText: clerkStyle({
        fontFamily: 'var(--vn-font-mono)',
        fontSize: '9px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--vn-text-muted)',
      }),
      otpCodeFieldInput: clerkStyle({
        width: '42px',
        height: '48px',
        color: 'var(--vn-text-primary)',
        WebkitTextFillColor: 'var(--vn-text-primary)',
        caretColor: accentSafe,
        background: 'var(--vn-surface-input)',
        border: '1px solid var(--vn-border-strong)',
        boxShadow: 'inset 0 0 0 1px var(--vn-border-default)',
        opacity: 1,
      }),
      footer: clerkStyle({
        background: 'transparent',
      }),
      footerActionText: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      footerActionLink: clerkStyle({
        color: accentSafe,
        fontWeight: 700,
      }),
      identityPreview: clerkStyle({
        minHeight: '42px',
        padding: '10px 12px',
        background: 'var(--vn-surface-raised)',
        border: '1px solid var(--vn-border-default)',
      }),
      identityPreviewText: clerkStyle({
        color: 'var(--vn-text-primary)',
        lineHeight: 1.45,
      }),
      identityPreviewEditButton: clerkStyle({
        color: accentSafe,
      }),
    },
  };
}

export type VitnessClerkAppearance = ReturnType<typeof createVitnessClerkAppearance>;

/**
 * Waitlist appearance — the base auth preset with Clerk's internal chrome
 * hidden: the header (Clerk's own "Join the waitlist" title + subtitle, which
 * the host duplicates with its own heading) and the footer (the redundant
 * "Already have access? Sign in" link and the "Secured by Clerk" badge — the
 * coming-soon waitlist has nothing to sign in to yet). Same adapter pattern as
 * the org/profile presets.
 */
export function createVitnessClerkWaitlistAppearance({ accent, accentSafe }: VitnessClerkTheme) {
  const base = createVitnessClerkAppearance({ accent, accentSafe });
  return {
    ...base,
    elements: {
      ...base.elements,
      header: clerkStyle({ display: 'none' }),
      footer: clerkStyle({ display: 'none' }),
      logoBox: clerkStyle({ display: 'none' }),
    },
  };
}

export type VitnessClerkWaitlistAppearance = ReturnType<typeof createVitnessClerkWaitlistAppearance>;

/**
 * Builds the full-surface Clerk appearance used for account management.
 * Unlike the authentication preset, this preserves the profile card's layout,
 * navigation, spacing, and content hierarchy.
 */
export function createVitnessClerkUserProfileAppearance({
  accent,
  accentSafe,
}: VitnessClerkTheme) {
  return {
    variables: createClerkVariables({ accent, accentSafe }),
    elements: {
      ...createClerkFormElements({ accent, accentSafe }),
      rootBox: clerkStyle({
        width: '100%',
        height: '100dvh',
      }),
      cardBox: clerkStyle({
        width: '100%',
        height: '100dvh',
        boxShadow: 'none',
      }),
      card: clerkStyle({
        width: '100%',
        height: '100dvh',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
      }),
      navbarButton: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      navbarButton__active: clerkStyle({
        color: accentSafe,
      }),
      navbarButtonIcon: clerkStyle({
        color: 'currentColor',
      }),
      headerTitle: clerkStyle({
        fontFamily: 'var(--vn-font-display)',
        color: 'var(--vn-text-primary)',
        letterSpacing: '-0.035em',
      }),
      headerSubtitle: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      profileSection: clerkStyle({
        borderBottom: '1px solid var(--vn-border-default)',
      }),
      profileSectionTitleText: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      profileSectionContent: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      profileSectionPrimaryButton: clerkStyle({
        color: accentSafe,
        fontWeight: 700,
      }),
      accordionTriggerButton: clerkStyle({
        color: 'var(--vn-text-primary)',
      }),
      userPreviewMainIdentifier: clerkStyle({
        color: 'var(--vn-text-primary)',
      }),
      userPreviewSecondaryIdentifier: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      avatarBox: clerkStyle({
        border: '1px solid var(--vn-border-strong)',
      }),
      badge: clerkStyle({
        color: accentSafe,
        background: 'var(--vn-surface-raised)',
        border: '1px solid var(--vn-border-default)',
      }),
    },
  };
}

export type VitnessClerkUserProfileAppearance = ReturnType<
  typeof createVitnessClerkUserProfileAppearance
>;

/**
 * Builds the compact Clerk appearance for popover controls — `UserButton` and
 * `OrganizationSwitcher`. Styles the trigger and popover surfaces with Vitness
 * tokens; the popover behaves like a Vitness dropdown (card surface, default
 * border, dropdown elevation).
 */
export function createVitnessClerkControlAppearance({
  accent,
  accentSafe,
}: VitnessClerkTheme) {
  const popoverCard = vitnessPopoverSurface;
  const popoverActionButton = vitnessPopoverItem;

  return {
    variables: createClerkVariables({ accent, accentSafe }),
    elements: {
      userButtonAvatarBox: clerkStyle({
        width: '28px',
        height: '28px',
        border: '1px solid var(--vn-border-strong)',
      }),
      // `showName` layout for the sidebar footer: avatar on the left, name on the
      // right, full-width trigger so it reads as a Vitness account chip.
      userButtonBox: clerkStyle({
        flexDirection: 'row-reverse',
        gap: '9px',
        width: '100%',
        justifyContent: 'flex-start',
      }),
      userButtonTrigger: clerkStyle({
        width: '100%',
        justifyContent: 'flex-start',
        padding: '2px',
        borderRadius: '6px',
      }),
      userButtonOuterIdentifier: clerkStyle({
        fontSize: '12px',
        fontWeight: 600,
        color: 'var(--vn-text-primary)',
      }),
      userButtonPopoverCard: popoverCard,
      userButtonPopoverActionButton: popoverActionButton,
      organizationSwitcherTrigger: clerkStyle({
        width: '100%',
        justifyContent: 'space-between',
        padding: '6px 8px',
        borderRadius: '6px',
        fontFamily: 'var(--vn-font-body)',
        fontSize: '12px',
        fontWeight: 650,
        color: 'var(--vn-text-primary)',
      }),
      organizationSwitcherTriggerIcon: clerkStyle({
        color: 'var(--vn-text-muted)',
      }),
      organizationSwitcherPopoverCard: popoverCard,
      organizationSwitcherPopoverActionButton: popoverActionButton,
      organizationPreviewAvatarBox: clerkStyle({
        border: '1px solid var(--vn-border-strong)',
      }),
      organizationPreviewMainIdentifier: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      organizationPreviewSecondaryIdentifier: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      userPreviewMainIdentifier: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      userPreviewSecondaryIdentifier: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
    },
  };
}

export type VitnessClerkControlAppearance = ReturnType<
  typeof createVitnessClerkControlAppearance
>;

/**
 * Builds the Clerk appearance for organization surfaces — `OrganizationList`
 * and the much heavier `OrganizationProfile` (members table, invitation flow,
 * role selectors, danger zone). Presents Clerk's card as a standard Vitness
 * card, reuses the shared form styling, and threads `--vn-*` tokens through
 * every surface so dark and light themes both resolve without overrides.
 */
export function createVitnessClerkOrganizationAppearance({
  accent,
  accentSafe,
}: VitnessClerkTheme) {
  // The role dropdown and row-action menus reuse the shared popover surface so
  // they read as Vitness cards, exactly like the control preset's popovers.
  const menuSurface = vitnessPopoverSurface;
  const menuItem = vitnessPopoverItem;

  return {
    variables: createClerkVariables({ accent, accentSafe }),
    elements: {
      ...createClerkFormElements({ accent, accentSafe }),
      cardBox: clerkStyle({
        boxShadow: 'var(--vn-shadow-card)',
      }),
      card: clerkStyle({
        background: 'var(--vn-surface-card)',
        border: '1px solid var(--vn-border-default)',
        borderRadius: '8px',
      }),
      headerTitle: clerkStyle({
        fontFamily: 'var(--vn-font-display)',
        letterSpacing: '-0.035em',
        color: 'var(--vn-text-primary)',
      }),
      headerSubtitle: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),

      // OrganizationProfile tab navigation (Members / Invitations / Settings).
      navbarButton: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      navbarButton__active: clerkStyle({
        color: accentSafe,
      }),
      navbarButtonIcon: clerkStyle({
        color: 'currentColor',
      }),

      organizationPreviewAvatarBox: clerkStyle({
        border: '1px solid var(--vn-border-strong)',
      }),
      organizationPreviewMainIdentifier: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      organizationPreviewSecondaryIdentifier: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      userPreviewMainIdentifier: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      userPreviewSecondaryIdentifier: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),

      // Members + invitations tables.
      tableHead: clerkStyle({
        color: 'var(--vn-text-secondary)',
        fontSize: '11px',
        fontWeight: 650,
        borderBottom: '1px solid var(--vn-border-default)',
      }),
      membersPageInviteButton: clerkStyle({
        minHeight: '38px',
        color: '#09090B',
        background: accent,
        boxShadow: 'none',
        fontSize: '12px',
        fontWeight: 750,
      }),

      // Role dropdown (change-role flow) + row-action menus (revoke invite).
      selectButton: clerkStyle({
        minHeight: '34px',
        color: 'var(--vn-text-primary)',
        background: 'var(--vn-surface-input)',
        border: '1px solid var(--vn-border-default)',
      }),
      selectButtonIcon: clerkStyle({
        color: 'var(--vn-text-muted)',
      }),
      selectOptionsContainer: menuSurface,
      selectOption: menuItem,
      menuList: menuSurface,
      menuItem,
      menuButton: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),

      paginationButton: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      paginationButtonIcon: clerkStyle({
        color: 'currentColor',
      }),

      profileSection: clerkStyle({
        borderBottom: '1px solid var(--vn-border-default)',
      }),
      profileSectionTitleText: clerkStyle({
        color: 'var(--vn-text-primary)',
        fontWeight: 700,
      }),
      profileSectionContent: clerkStyle({
        color: 'var(--vn-text-secondary)',
      }),
      profileSectionPrimaryButton: clerkStyle({
        color: accentSafe,
        fontWeight: 700,
      }),
      // Danger zone (leave / delete organization) must read as danger, never accent.
      profileSectionPrimaryButton__danger: clerkStyle({
        color: 'var(--vn-danger)',
        fontWeight: 700,
      }),
      badge: clerkStyle({
        color: accentSafe,
        background: 'var(--vn-surface-raised)',
        border: '1px solid var(--vn-border-default)',
      }),
      avatarBox: clerkStyle({
        border: '1px solid var(--vn-border-strong)',
      }),
    },
  };
}

export type VitnessClerkOrganizationAppearance = ReturnType<
  typeof createVitnessClerkOrganizationAppearance
>;

/**
 * Returns the Clerk Appearance preset for the active Vitness theme.
 */
export function useVitnessClerkAppearance(): VitnessClerkAppearance {
  const { accent, accentSafe } = useTheme();

  return useMemo(
    () => createVitnessClerkAppearance({ accent, accentSafe }),
    [accent, accentSafe],
  );
}

/**
 * Returns the Clerk Waitlist appearance preset for the active Vitness theme.
 */
export function useVitnessClerkWaitlistAppearance(): VitnessClerkWaitlistAppearance {
  const { accent, accentSafe } = useTheme();

  return useMemo(
    () => createVitnessClerkWaitlistAppearance({ accent, accentSafe }),
    [accent, accentSafe],
  );
}

/**
 * Returns the Clerk UserProfile appearance preset for the active Vitness theme.
 */
export function useVitnessClerkUserProfileAppearance(): VitnessClerkUserProfileAppearance {
  const { accent, accentSafe } = useTheme();

  return useMemo(
    () => createVitnessClerkUserProfileAppearance({ accent, accentSafe }),
    [accent, accentSafe],
  );
}

/**
 * Returns the popover-control appearance preset (UserButton /
 * OrganizationSwitcher) for the active Vitness theme.
 */
export function useVitnessClerkControlAppearance(): VitnessClerkControlAppearance {
  const { accent, accentSafe } = useTheme();

  return useMemo(
    () => createVitnessClerkControlAppearance({ accent, accentSafe }),
    [accent, accentSafe],
  );
}

/**
 * Returns the organization-surface appearance preset (OrganizationList /
 * OrganizationProfile) for the active Vitness theme.
 */
export function useVitnessClerkOrganizationAppearance(): VitnessClerkOrganizationAppearance {
  const { accent, accentSafe } = useTheme();

  return useMemo(
    () => createVitnessClerkOrganizationAppearance({ accent, accentSafe }),
    [accent, accentSafe],
  );
}
